import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";

import {
    Popover,
    Spinner,
    SplitView,
    SplitViewListbox,
    Tooltip,
} from "@salesforce/design-system-react";

import { Button, Modal } from "@vimeo/iris/components";
import { Link, DownloadArrow, ArrowLeft, Scissors } from "@vimeo/iris/icons";

import { ListResponse, EmbedPreset, VideoFile } from "vimeo";

import { getContentBlockType, isContentBlockContext } from "../sessionStorage";
import VideoSettingsContext, {
    VideoSettingsState,
} from "../contexts/videoSettingsContext";

import { client as vimeoClient, getVideo } from "../vimeoClient";
import { CopyVideoLink } from "../views/CopyVideoLink";

import { getRootPath, onTrimVideoClicked } from "../common";

import VimeoLogo from "../assets/images/vimeo_logo_dark.png";

import { ModalStyled } from "../components/modal.styles";
import { ModalHeader } from "../components/ModalHeader";

import "./settings.scoped.scss";

import { Data } from "blocksdk";

import UserContext, { UserContextState } from "../contexts/userContext";
import {
    EventName,
    sendAnalyticsEvent,
    WorkflowEventNames,
} from "../analytics/common";

interface ListItemProps {
    id: string;
    label: string;
    value: string;
}

function ListItem(props: any) {
    return (
        <div className="slds-text-heading_small">
            {(props.item as ListItemProps).label}
        </div>
    );
}

export function Settings() {
    const params = useParams();

    const [userContextState, setUserContextState] = useState<UserContextState>(
        {}
    );
    const [title, setTitle] = useState<string>();
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [selected, setSelected] = useState<ListItemProps[]>();

    // This is only used in the web content block context.
    const location = useLocation();
    const [selectedToggleButton, setSelectedToggleButton] = useState(
        location.pathname.endsWith("basic") ? "basic" : "embed-for-web"
    );

    const [showCopyLinkPopover, setShowCopyLinkPopover] = useState(false);
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [hideBackButton, setHideBackButton] = useState(false);

    const showSplitView = isContentBlockContext();
    const isWebContentBlockContext =
        isContentBlockContext() && getContentBlockType() === "web";

    // Hide the heading since we are in the context of a web content block which
    // has a toggle button at the top that clearly shows which settings view
    // the user is currently looking at.
    const [videoSettingsState, setSetVideoSettingsState] =
        useState<VideoSettingsState>({
            hideSettingsViewHeading: isWebContentBlockContext,
        });

    async function getEmbedPresets(): Promise<EmbedPreset[]> {
        const resp = await vimeoClient.get<ListResponse<EmbedPreset>>(
            `/api/vimeo/user/me/presets`
        );
        return resp.data.data;
    }

    // If the location changes through a navigation, we should update
    // the currently selected button as well.
    useEffect(() => {
        setSelectedToggleButton(
            location.pathname.endsWith("basic") ? "basic" : "embed-for-web"
        );
    }, [location]);

    useEffect(() => {
        setTitle(decodeURIComponent(videoSettingsState.video?.name || ""));

        getEmbedPresets().then((presets) => {
            setUserContextState({ ...userContextState, presets });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [videoSettingsState]);

    useEffect(() => {
        const videoId = params.videoId;
        if (!videoId) {
            return;
        }

        setLoading(true);
        getVideo(videoId)
            .then((v) => {
                setSetVideoSettingsState({
                    ...videoSettingsState,
                    video: v,
                });
            })
            .catch(console.error)
            .finally(() => setLoading(false));
        // Make sure to watch only explicit properties from the
        // `params` object since the user navigating between views
        // in the split-view will change the whole URL, so we
        // shouldn't watch the whole `params` object. Watching the
        // whole object will cause unnecessary re-renders even though
        // the user is still viewing the settings for the same video.
        //
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.videoId]);

    useEffect(() => {
        if (!showDownloadModal || !videoSettingsState.video) {
            return;
        }

        // Track opening the Downloads modal.
        sendAnalyticsEvent(WorkflowEventNames.openDownloadModal, {
            actor_resource_role: null, //constant
            actor_team_role: null, //constant
            entry_page: null, //constant
            is_preset_applied: null, //constant
            page: null, //constant
            path: null, //constant
            team_owner_id: null, //constant
            team_size: null, //constant
            team_subscription_type: null, //constant
            upload_id: null, //constant
            video_app_id: null, //constant
            video_status: null, //constant
            video_type: null, //constant
            is_owner: null, //constant
            is_my_videos: null, //constant

            is_video_password_protected: !!videoSettingsState.video.password,
            location: "video_settings", //constant
            number_of_files: videoSettingsState.video.download?.length || 0,
            video_id: params.videoId,
            video_embed_privacy: videoSettingsState.video.privacy.embed,
            video_privacy: videoSettingsState.video.privacy.view,
        }, 4);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showDownloadModal]);

    useEffect(() => {
        if (!showCopyLinkPopover || !videoSettingsState.video) {
            return;
        }

        // Track opening the Copy Link modal.
        sendAnalyticsEvent(WorkflowEventNames.openCopyLinkModal, {
            actor_resource_role: null, //constant
            actor_team_role: null, //constant
            entry_page: null, //constant
            is_preset_applied: null, //constant
            page: null, //constant
            path: null, //constant
            team_owner_id: null, //constant
            team_size: null, //constant
            team_subscription_type: null, //constant
            upload_id: null, //constant
            video_app_id: null, //constant
            video_status: null, //constant
            video_type: null, //constant

            is_video_password_protected: !!videoSettingsState.video.password,
            location: "video_settings", //constant
            video_id: params.videoId,
            video_embed_privacy: videoSettingsState.video.privacy.embed,
            video_privacy: videoSettingsState.video.privacy.view,
        }, 3);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showCopyLinkPopover]);

    function listView() {
        return [
            <SplitViewListbox
                key="1"
                events={{
                    onSelect: (event: any, val: any) => {
                        setSelected(val.selectedItems);
                        // Always replace the current route in the history,
                        // so that when the user wants to navigate back
                        // they go back to the "root". The root will depend
                        // on whether or not this app was loaded using a
                        // content block widget.
                        navigate(`${val.item.value}`, {
                            replace: true,
                        });
                    },
                }}
                options={[
                    { id: "basic", label: "Basic", value: "basic" },
                    {
                        id: "thumbnails",
                        label: "Thumbnails",
                        value: "thumbnails",
                    },
                    { id: "gifs", label: "GIFs", value: "gifs" },
                    {
                        id: "embed-for-web",
                        label: "Embed for web",
                        value: "embed-for-web",
                    },
                ]}
                listItem={ListItem}
                selection={selected}
            />,
        ];
    }

    function trackVideoDownload(videoFile: VideoFile) {
        if (!videoSettingsState.video) {
            return;
        }

        sendAnalyticsEvent(WorkflowEventNames.downloadVideoFile, {
            actor_resource_role: null, //constant
            actor_team_role: null, //constant
            entry_page: null, //constant
            is_preset_applied: null, //constant
            page: null, //constant
            path: null, //constant
            team_owner_id: null, //constant
            team_size: null, //constant
            team_subscription_type: null, //constant
            upload_id: null, //constant
            video_app_id: null, //constant
            video_status: null, //constant
            video_type: null, //constant
            is_owner: null, //constant

            // TODO: How do we get the aspect ratio?
            aspect_ratio: null,
            file_size: videoFile.size / 1024 / 1024, //size of video (mb)
            height: videoFile.height,
            is_video_password_protected: !!videoSettingsState.video.password,
            location: "video_settings", //constant
            quality: videoFile.rendition,
            quality_type: videoFile.quality,
            resolution: `${videoFile.width} x ${videoFile.height}`,
            type: videoFile.type,
            video_id: params.videoId,
            video_embed_privacy: videoSettingsState.video.privacy.embed,
            video_privacy: videoSettingsState.video.privacy.view,
            width: videoFile.width,
        }, 3);
    }

    const downloads = videoSettingsState.video?.download
        ?.sort(
            (a, b) => (a.height > b.height ? -1 : a.height === b.height ? 0 : 1) // Sort in descending order.
        )
        .map((f, idx) => {
            return (
                <div
                    key={idx}
                    style={{ display: "flex", flexFlow: "row nowrap" }}
                    className="slds-var-m-bottom_medium"
                >
                    <div
                        style={{
                            display: "flex",
                            flexFlow: "column nowrap",
                            flex: 0.5,
                        }}
                    >
                        <div className="sldsslds-text-heading_small">
                            <strong>{f.rendition}</strong>
                        </div>
                        <div>
                            <strong>{f.size_short}</strong> | {f.width} x{" "}
                            {f.height}
                        </div>
                    </div>
                    <div
                        style={{
                            display: "flex",
                            flex: 0.5,
                            justifyContent: "end",
                        }}
                    >
                        <a href={f.link} onClick={() => trackVideoDownload(f)} download={title}><DownloadArrow className="download-icon"/></a>
                    </div>
                </div>
            );
        });

    function onSwitchSettingsClicked(value: string) {
        // Always replace the current route in the history,
        // so that when the user wants to navigate back
        // they go back to the "root". The root will depend
        // on whether or not this app was loaded using a
        // content block widget.
        navigate(value, { replace: true });
        setSelectedToggleButton(value);
    }

    // Hide the back button if the user clicked on an
    // existing content block in CloudPages.
    // It's because we would automatically navigate the
    // user to the settings view for the video that is
    // currently configured in the content block anyway,
    // so they cannot navigate back.
    if (getContentBlockType() === "web") {
        window.blockSdk.getData((data: Data) => {
            if (data.web?.videoUri) {
                setHideBackButton(true);
            }
        });
    }

    return (
        <div className="slds-var-p-around_medium">
            {loading && <Spinner />}
            <header className="slds-global-header_container">
                <div className="slds-global-header slds-grid slds-grid_align-spread">
                    <div
                        className="slds-global-header__item"
                        style={{ maxWidth: "60%" }}
                    >
                        <div className="slds-grid">
                            {!hideBackButton && (
                                <div className="slds-var-m-right_small">
                                    <Button
                                        format="secondary"
                                        variant="minimal"
                                        icon={<ArrowLeft color="#000000" />}
                                        onClick={() => navigate(getRootPath())}
                                        size="xs"
                                    />
                                </div>
                            )}
                            <div className="slds-var-m-right_small">
                                <img
                                    className="slds-var-m-bottom_xx-small"
                                    src={VimeoLogo}
                                    alt="Vimeo"
                                    style={{ width: "64px", minWidth: "64px" }}
                                />
                            </div>
                            <div
                                className="slds-col slds-var-m-right_small"
                                style={{ color: "#DEE4E9" }}
                            >
                                |
                            </div>
                            <div
                                className="video-title slds-text-heading_small slds-truncate"
                                title={title}
                            >
                                {title}
                            </div>
                        </div>
                    </div>
                    <div className="slds-global-header__item">
                        <div className="slds-grid">
                            <div className="slds-m-horizontal_xx-small">
                                <Popover
                                    align="bottom"
                                    hasNoNubbin={true}
                                    style={{
                                        marginTop: "8px",
                                        right: "200px",
                                        width: "300px",
                                    }}
                                    body={
                                        <CopyVideoLink
                                            video={videoSettingsState.video!}
                                        />
                                    }
                                    heading="Copy Link"
                                    isOpen={showCopyLinkPopover}
                                    onRequestClose={() =>
                                        setShowCopyLinkPopover(
                                            !showCopyLinkPopover
                                        )
                                    }
                                >
                                    <Tooltip content="Copy video link">
                                        <Button
                                            format="primary"
                                            icon={<Link />}
                                            onClick={() =>
                                                setShowCopyLinkPopover(
                                                    !showCopyLinkPopover
                                                )
                                            }
                                            size="xs"
                                        />
                                    </Tooltip>
                                </Popover>
                            </div>
                            <Tooltip content="Download video">
                                <div className="slds-m-horizontal_xx-small">
                                    <Button
                                        format="primary"
                                        icon={<DownloadArrow />}
                                        onClick={() =>
                                            setShowDownloadModal(
                                                !showDownloadModal
                                            )
                                        }
                                        size="xs"
                                    />
                                </div>
                            </Tooltip>
                            <Tooltip content="Trim video">
                                <div className="slds-m-horizontal_xx-small">
                                    <Button
                                        format="primary"
                                        icon={<Scissors />}
                                        onClick={() => {
                                            sendAnalyticsEvent(
                                                EventName.integrationsClick,
                                                {
                                                    name: "open_trimmer_modal",
                                                    location: "video_settings",
                                                    video_id: params.videoId,
                                                    cta: "trim_video",
                                                }
                                            );
                                            onTrimVideoClicked(params.videoId);
                                        }}
                                        size="xs"
                                    />
                                </div>
                            </Tooltip>
                        </div>
                    </div>
                </div>
            </header>
            <div style={{ marginTop: "36px", height: "90vh" }}>
                {isWebContentBlockContext && (
                    <div
                        className="slds-align_absolute-center"
                        style={{ marginTop: "3rem" }}
                    >
                        <div style={{ display: "flex" }}>
                            <Button
                                className="slds-var-m-right_small"
                                onClick={() => onSwitchSettingsClicked("basic")}
                                format={
                                    selectedToggleButton === "basic"
                                        ? "primary"
                                        : "secondary"
                                }
                            >
                                Basic
                            </Button>
                            <Button
                                onClick={() =>
                                    onSwitchSettingsClicked("embed-for-web")
                                }
                                format={
                                    selectedToggleButton === "embed-for-web"
                                        ? "primary"
                                        : "secondary"
                                }
                            >
                                Embed for web
                            </Button>
                        </div>
                    </div>
                )}
                {
                    // Don't use the split view if we are showing the settings
                    // page in the context of a content block editor.
                }
                {!showSplitView ? (
                    <SplitView
                        id="settingsSplitView"
                        master={listView()}
                        detail={
                            <div
                                className="slds-var-p-around_medium"
                                style={{ height: "100%" }}
                            >
                                <UserContext.Provider
                                    value={[
                                        userContextState,
                                        setUserContextState,
                                    ]}
                                >
                                    <VideoSettingsContext.Provider
                                        value={[
                                            videoSettingsState,
                                            setSetVideoSettingsState,
                                        ]}
                                    >
                                        <Outlet />
                                    </VideoSettingsContext.Provider>
                                </UserContext.Provider>
                            </div>
                        }
                    />
                ) : (
                    <div className="slds-var-p-around_medium">
                        <UserContext.Provider
                            value={[userContextState, setUserContextState]}
                        >
                            <VideoSettingsContext.Provider
                                value={[
                                    videoSettingsState,
                                    setSetVideoSettingsState,
                                ]}
                            >
                                <Outlet />
                            </VideoSettingsContext.Provider>
                        </UserContext.Provider>
                    </div>
                )}
            </div>
            <Modal
                active={showDownloadModal}
                onClose={() => setShowDownloadModal(!showDownloadModal)}
                style={{
                    width: "340px",
                    maxHeight: "450px",
                    overflow: "auto",
                }}
                content={
                    <ModalStyled>
                        <ModalHeader
                            onCloseClicked={() =>
                                setShowDownloadModal(!showDownloadModal)
                            }
                            title="Download"
                        ></ModalHeader>
                        <div>
                            {downloads && downloads.length ? (
                                downloads
                            ) : (
                                <span className="slds-align_absolute-center">
                                    There are no files to download for this
                                    video.
                                </span>
                            )}
                        </div>
                    </ModalStyled>
                }
            ></Modal>
        </div>
    );
}
